/**
 * Centralized, active Network Status monitor.
 * Employs fallback ping controls to confirm internet reachability beyond mere loopback reports.
 */

export type OnlineStateCallback = (isOnline: boolean) => void;

class NetworkManager {
  private isOnlineState: boolean = typeof navigator !== 'undefined' ? navigator.onLine : true;
  private listeners = new Set<OnlineStateCallback>();

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.updateState(true));
      window.addEventListener('offline', () => this.updateState(false));
      
      // Perform initial reachability verification
      this.verifyRealConnectivity();
    }
  }

  /**
   * Return instantaneous online/offline report.
   */
  public isOnline(): boolean {
    return this.isOnlineState;
  }

  /**
   * Set up reactive network transitions.
   */
  public subscribe(callback: OnlineStateCallback): () => void {
    this.listeners.add(callback);
    callback(this.isOnlineState);
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Proactively ping an external lightweight URL (like favicon or health checkout)
   * to ensure actual WAN connectivity, protecting against fake Wi-Fi/captive portals.
   */
  public async verifyRealConnectivity(): Promise<boolean> {
    if (typeof navigator === 'undefined' || !navigator.onLine) {
      this.updateState(false);
      return false;
    }

    try {
      // Small fast fetch payload
      const response = await fetch('/logo_light.png?ping=' + Date.now(), {
        method: 'HEAD',
        cache: 'no-store',
      });
      const realOnline = response.ok;
      this.updateState(realOnline);
      return realOnline;
    } catch {
      this.updateState(false);
      return false;
    }
  }

  private updateState(newState: boolean): void {
    if (this.isOnlineState !== newState) {
      this.isOnlineState = newState;
      console.log(`[NetworkManager] Connection status altered. Online = ${newState}`);
      this.listeners.forEach((listener) => {
        try {
          listener(newState);
        } catch (error) {
          console.error('[NetworkManager] Error executing state listener:', error);
        }
      });
    }
  }
}

export const networkManager = new NetworkManager();
export default networkManager;
